import { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { removeBootReturnCover } from '../../utils/billing/bootReturnCover';
import { C, AUTH_RHYTHM, AUTH_BRAND_FONT, authTitleStyle, useLoginLayoutMode } from './loginAuthTheme';
import { overlayLayers } from '../../ui/overlayLayers';
import { foundation } from '../../ui/tokens';
import { useResponsiveLayout } from '../../hooks/ui/useResponsiveLayout';
import BrandLogo from '../common/BrandLogo';
import LoginAccessCard from './LoginAccessCard';
import { useSplashGlideTarget } from '../../ui/splash/useSplashGlideTarget';
import { SPLASH_PHASE, useSplashPhase } from '../../ui/splash/splashHandoff';
import { MFA_GATE_GLIDE_MS, playMfaGateEntrance } from './mfa/mfaGateEntrance';
import { useMfaEnrollmentHandoff } from './mfa/useMfaEnrollmentHandoff';
import { SPLASH_GLIDE_PRESETTLED_ATTR } from '../../ui/splash/splashGlide';

const SANS = foundation.fontFamily.sans;
// The stepper's own 24px rhythm (mfaStyles MFA_GUTTER), shared with the
// Account modal — kept local to the gate via LoginAccessCard's `chrome` prop.
const GATE_GUTTER = foundation.spacePx[6];

/**
 * Gate-only LoginAccessCard geometry (2026-09-25 gate audit): 24px gutters in
 * header/body/footer (matches the stepper's optical back/✕ inset), the brand
 * placed like the sign-in screens (2rem wordmark, 18px to the next line,
 * 30px/36px card top), header padding-bottom 0 so the stepper wrapper's own
 * 20px margin is the WHOLE bars → title gap, 20px body → footer, and the
 * header left-aligned (only the brand stays centered).
 */
function gateChrome(isMobile) {
  return {
    gutter: GATE_GUTTER,
    headerPadTop: isMobile ? '30px' : '36px',
    headerPadBottom: '0px',
    headerAlign: 'left',
    iconGap: `${AUTH_RHYTHM.brandToTitleCompact}px`,
    bodyPadBottomWithFooter: foundation.spacePx[5],
    footerPad: `${foundation.spacePx[4]} ${GATE_GUTTER}`,
  };
}

/**
 * MfaEnrollmentGateShell — shared chrome for the forced MFA enrollment gates
 * (tenant admin + platform super-admin). Now rendered on the login design
 * language: washed C.bg shell, bounded white card, BrandLogo + SORA header,
 * pinned-header / scroll-body / pinned-footer pattern.
 *
 * Non-dismissible: no scrim-click handler is wired, so the only exits are
 * completing enrollment or signing out.
 *
 * keyboardAware is inherited from LoginAccessCard → AnimatedModalShell so the
 * 6-digit code field reframes above the iOS keyboard.
 *
 * Splash handoff (RD-SEC-021 / KAN-651, silk RD-SEC-021b / KAN-655,
 * touch RD-SEC-021c / KAN-656): same logoJourney pattern as role welcome —
 * under the first splash cover the wordmark is the glide target, the card
 * mounts at rest (`skipEnter` + fade) and settles BEFORE the splash leaves,
 * then the splash fade + logo glide reveal it as ONE continuous wordmark.
 * Pre-settled (skip leave settle) is DESKTOP-ONLY — on phone/tablet the
 * touch band still recenters the card, so leave settle stays (welcome
 * contract). Mid-session (no splash) keeps the scale entrance.
 *
 * RD-SEC-015: `title` is now OPTIONAL — the stepper supplies its own header
 * (back/label/close + progress bars) via `stepHeader` and its own
 * intro-step title/subtitle, so the gates no longer pass a redundant
 * branded title/body here. Pass an explicit `ariaLabel` in that case (falls
 * back to `title` when it's a string, as before) so the dialog still has an
 * accessible name.
 *
 * RD-SEC-015 (2026-09-25 anatomy fix): `stepHeader` — the stepper's own
 * `header` node (from `useMfaStepperUi`) — renders in LoginAccessCard's
 * PINNED header region (below the brand icon), raw/unwrapped (it carries its
 * own typography, unlike a plain string `title`). This replaces the old
 * `position: sticky` header that used to sit INSIDE the scrollable body.
 * `footer` — the stepper's own CTA (Get started / I added it / Verify /
 * Continue / Done…) — is stacked ABOVE the sign-out link in LoginAccessCard's
 * PINNED footer region; `hideFooter` still controls ONLY the sign-out link
 * (the CTA always shows, same as every step needing SOME way forward).
 */
export default function MfaEnrollmentGateShell({
  title,
  body,
  stepHeader,
  onSignOut,
  signOutLabel,
  footer,
  hideFooter = false,
  ariaLabel,
  children,
}) {
  const { isMobile, isTablet } = useResponsiveLayout();
  useLayoutEffect(() => { removeBootReturnCover(); }, []); // KAN-877: the 2FA gate always wins over the Stripe-return boot cover
  const compact = isMobile || isTablet;
  // Same breakpoint as AuthScreenShell's wordmark, so login → gate never changes size.
  const brandFont = useLoginLayoutMode() === 'mobile' ? AUTH_BRAND_FONT.mobile : AUTH_BRAND_FONT.card;
  const { handoffEntrance, holdLive } = useMfaEnrollmentHandoff();
  const glide = useSplashGlideTarget(MFA_GATE_GLIDE_MS);
  const phase = useSplashPhase();
  const handoffAtMountRef = useRef(handoffEntrance);
  const cancelEntranceRef = useRef(null);

  // Same contract as RoleWelcomeHeader: start the content stagger on the
  // frame the splash starts LEAVING (t0 of the logo glide). Opacity only.
  // Wordmark lives inside the portalled dialog — find the panel from it.
  useLayoutEffect(() => {
    if (!handoffAtMountRef.current || cancelEntranceRef.current || phase === SPLASH_PHASE.COVERED) return;
    const wm = typeof document !== 'undefined'
      ? document.querySelector('[data-testid="mfa-gate-wordmark"]')
      : null;
    cancelEntranceRef.current = playMfaGateEntrance(wm?.closest('[role="dialog"]'));
  }, [phase]);
  useLayoutEffect(() => () => cancelEntranceRef.current?.(), []);

  // Same wordmark size as AuthScreenShell's sign-in brand. Hidden while the
  // splash logoJourney flies onto it (welcome header contract).
  // Pre-settled (RD-SEC-021b/c): desktop only — skips leave's second settle
  // so Chrome/Safari web has no double-pause. Touch MUST NOT mark it: the
  // MFA card still recenters when `--cvx-modal-*-reserve` settles (same as
  // role welcome); skipping leave settle aims mid-band → hitch on PWA.
  const icon = (
    <span
      ref={glide.ref}
      data-testid="mfa-gate-wordmark"
      {...glide.targetProps}
      {...(handoffEntrance && !holdLive && !compact
        ? { [SPLASH_GLIDE_PRESETTLED_ATTR]: '' }
        : null)}
      style={glide.hiddenStyle || undefined}
    >
      <BrandLogo fontSize={brandFont} crewColor={C.heading} />
    </span>
  );

  const signOutFooter = (
    <button
      type="button"
      onClick={onSignOut}
      style={{
        border: 'none',
        background: 'transparent',
        color: C.readable,
        fontSize: '0.8125rem',
        fontWeight: 500,
        fontFamily: SANS,
        cursor: 'pointer',
        padding: '8px 12px',
        minHeight: compact ? 44 : 36,
        marginTop: foundation.spacePx[1],
        borderRadius: '10px',
        textDecoration: 'underline',
        alignSelf: 'center',
      }}
      data-testid="mfa-gate-sign-out"
    >
      {signOutLabel}
    </button>
  );

  const resolvedFooter = footer || !hideFooter
    ? (
      <>
        {footer}
        {!hideFooter ? signOutFooter : null}
      </>
    )
    : null;

  const resolvedTitle = stepHeader ? undefined : (
    title == null ? undefined : (
      // "Option A / Crisper" title (2026-09-24), with ONE deliberate
      // exception: marginTop stays 0 (not authTitleStyle's 16px) — the
      // 8px logo->title gap here comes from LoginAccessCard's OWN icon
      // wrapper margin, a shared component also used outside the
      // pre-auth screens (admin settings), so it's left untouched.
      <span style={{ ...authTitleStyle(isMobile), marginTop: 0 }}>
        {title}
      </span>
    )
  );

  return (
    <LoginAccessCard
      isOpen
      zIndex={overlayLayers.mfaEnrollmentGate}
      // Calm (KAN-756): the gate never zooms in — mid-session it fades in
      // like the sign-in card does; after the splash it opens at rest.
      panelMotion="fade"
      skipEnter={handoffEntrance}
      icon={icon}
      title={resolvedTitle}
      headerContent={stepHeader || undefined}
      chrome={stepHeader ? gateChrome(isMobile) : undefined}
      subtitle={stepHeader ? undefined : body}
      footer={resolvedFooter}
      ariaLabel={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
    >
      {children}
    </LoginAccessCard>
  );
}

MfaEnrollmentGateShell.propTypes = {
  title: PropTypes.node,
  body: PropTypes.node,
  stepHeader: PropTypes.node,
  onSignOut: PropTypes.func.isRequired,
  signOutLabel: PropTypes.node.isRequired,
  footer: PropTypes.node,
  hideFooter: PropTypes.bool,
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
};
